import React from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { TableSizeOptions } from '../../components/TableSizeFilter/constants';
import { LimitOptions } from '../../components/LimitFilter/constants';
import {
  getPresetFiltersLocalStorage,
  setPresetFiltersLocalStorage,
} from '../../localStorage/presetFiltersLocalStorage';
import V2Icon from '../components/V2Icon';
import V2Select from '../components/V2Select';

const periods = [
  { value: 0, label: 'settings_period_all' },
  { value: 1, label: 'settings_period_month' },
  { value: 2, label: 'settings_period_three_months' },
  { value: 3, label: 'settings_period_six_months' },
  { value: 4, label: 'settings_period_year' },
  { value: 'custom', label: 'settings_period_custom' },
] as const;

const validDay = (value: unknown): value is string =>
  typeof value === 'string' &&
  /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  dayjs(value).isValid() &&
  dayjs(value).format('YYYY-MM-DD') === value;

function readFilters() {
  const defaults = { format: 0, limit: 0, period: 0 as number | 'custom', from: '', to: '' };
  try {
    const stored = getPresetFiltersLocalStorage();
    if (!stored || typeof stored !== 'object') return defaults;
    const custom = stored.customDateFilterValue;
    return {
      format: TableSizeOptions.some((option) => option.id === stored.tableSizeFilterValue)
        ? stored.tableSizeFilterValue!
        : 0,
      limit: LimitOptions.some((option) => option.id === stored.limitFilterValue)
        ? stored.limitFilterValue
        : 0,
      period:
        custom && validDay(custom.fromDate) && validDay(custom.toDate)
          ? ('custom' as const)
          : periods.some((option) => option.value === stored.dateFilterValue)
            ? stored.dateFilterValue!
            : 0,
      from: custom && validDay(custom.fromDate) ? custom.fromDate : '',
      to: custom && validDay(custom.toDate) ? custom.toDate : '',
    };
  } catch {
    return defaults;
  }
}

const V2FilterSettings: React.FC = () => {
  const { t } = useTranslation('translation');
  const id = React.useId();
  const fromRef = React.useRef<HTMLInputElement>(null);
  const toRef = React.useRef<HTMLInputElement>(null);
  const [values, setValues] = React.useState(readFilters);
  const [dateError, setDateError] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const update = (patch: Partial<typeof values>) => {
    setValues((current) => ({ ...current, ...patch }));
    setDateError(false);
    setSaved(false);
  };

  const apply = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const custom = values.period === 'custom';
    if (custom && (!validDay(values.from) || !validDay(values.to))) {
      setDateError(true);
      setSaved(false);
      (!validDay(values.from) ? fromRef : toRef).current?.focus();
      return;
    }
    const [fromDate, toDate] = [values.from, values.to].sort();
    setPresetFiltersLocalStorage(
      values.format || undefined,
      values.limit,
      custom || !values.period ? undefined : Number(values.period),
      custom ? { fromDate, toDate } : undefined,
    );
    if (custom) setValues((current) => ({ ...current, from: fromDate, to: toDate }));
    setDateError(false);
    setSaved(true);
  };

  return (
    <form className="v2-settings-panel" aria-labelledby={`${id}-title`} noValidate onSubmit={apply}>
      <header className="v2-settings-panel__head">
        <span className="v2-settings-panel__icon">
          <V2Icon name="filter" />
        </span>
        <div>
          <h2 id={`${id}-title`}>{t('preset_filters')}</h2>
          <p className="v2-settings-panel__description">{t('settings_filters_hint')}</p>
        </div>
      </header>
      <div className="v2-settings-filter-grid">
        <fieldset className="v2-settings-field">
          <legend>{t('table_size')}</legend>
          <div className="v2-settings-choices">
            {[{ id: 0, label: t('settings_all_formats') }, ...TableSizeOptions].map((option) => (
              <label className="v2-settings-choice" key={option.id}>
                <input
                  type="radio"
                  name={`${id}-format`}
                  value={option.id}
                  checked={values.format === option.id}
                  onChange={() => update({ format: option.id })}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <label className="v2-settings-field">
          <span>{t('limits')}</span>
          <V2Select
            value={values.limit}
            onChange={(limit) => update({ limit })}
            ariaLabel={t('limits')}
            options={LimitOptions.map((option) => ({
              value: option.id,
              label: option.id === 0 ? t('settings_all_limits') : `NL ${option.label}`,
            }))}
          />
        </label>
      </div>
      <fieldset className="v2-settings-field">
        <legend>{t('dates')}</legend>
        <div className="v2-settings-choices">
          {periods.map((period) => (
            <label className="v2-settings-choice" key={period.value}>
              <input
                type="radio"
                name={`${id}-period`}
                value={period.value}
                checked={values.period === period.value}
                onChange={() => update({ period: period.value })}
              />
              <span>{t(period.label)}</span>
            </label>
          ))}
        </div>
      </fieldset>
      {values.period === 'custom' ? (
        <div className="v2-settings-dates">
          <label className="v2-settings-field">
            <span>{t('settings_date_from')}</span>
            <input
              ref={fromRef}
              className="v2-settings-input"
              type="date"
              value={values.from}
              required
              aria-invalid={(dateError && !validDay(values.from)) || undefined}
              aria-describedby={dateError ? `${id}-date-error` : undefined}
              onChange={(event) => update({ from: event.target.value })}
            />
          </label>
          <label className="v2-settings-field">
            <span>{t('settings_date_to')}</span>
            <input
              ref={toRef}
              className="v2-settings-input"
              type="date"
              value={values.to}
              required
              aria-invalid={(dateError && !validDay(values.to)) || undefined}
              aria-describedby={dateError ? `${id}-date-error` : undefined}
              onChange={(event) => update({ to: event.target.value })}
            />
          </label>
          {dateError ? (
            <p className="v2-settings-field__error" id={`${id}-date-error`} role="alert">
              {t('settings_date_range_error')}
            </p>
          ) : null}
        </div>
      ) : null}
      <footer className="v2-settings-panel__footer">
        <p className="v2-settings-status" role="status">
          {saved ? t('settings_saved') : ''}
        </p>
        <button
          type="button"
          className="outline-button"
          onClick={() => update({ format: 0, limit: 0, period: 0, from: '', to: '' })}
        >
          {t('reset')}
        </button>
        <button type="submit" className="acid-button">
          {t('apply')}
        </button>
      </footer>
    </form>
  );
};

export default V2FilterSettings;
